<template>
    <component :is="Head">
        <html :lang="htmlLang" />
        <title>{{ siteStore.site.name }}</title>
        <link rel="icon" type="image/x-icon" :href="siteStore.site.logoMobileUrl" />

        <body class="app-body" />
    </component>
    <div>
        <UiLoader v-if="isLoadingComposable || isRestoringSave" fullscreen opaque>{{ t('Loading') }}</UiLoader>
        <router-view v-else />
    </div>
    <UiDialog />
    <UiToastContainer />
    <VdpGuideModal />
</template>

<script lang="ts" setup>
import { ConfirmationRoute } from './types/ConfirmationRoute';
import { useAppInit } from '@/composables/useAppInit';
import { useViewportInsets } from '@/composables/useViewportInsets';
import { useCustomFonts } from '@/composables/useCustomFonts';
import { useCatalogPricingStore } from '@/stores/catalogPricing';
import { parseMockupQueryFlag } from '@/composables/mockupExport';
const { initApp, isLoading: isLoadingComposable } = useAppInit();

const siteStore = useSiteStore();
const productStore = useProductStore();
const stageStore = useStageStore();
const route = useRoute();
const router = useRouter();
const { t } = i18n.global;
const { confirm } = useDialog();

// Langue déclarée sur <html lang> : DOIT refléter la locale i18n active (es, fr,
// de…) pour l'accessibilité et le SEO. `i18n.global.locale` est un Ref (mode
// Composition, legacy:false) : lié tel quel dans le template (accès imbriqué,
// non déballé par Vue) il produisait un `lang` invalide. On lit donc `.value`
// via un computed réactif (mis à jour à chaque changement de langue).
const htmlLang = computed(() => i18n.global.locale.value);

// Reprise via ?save= : on capture l'id depuis l'URL INITIALE (robuste à une éventuelle
// redirection qui viderait route.query) et on affiche le loader plein écran DÈS le setup
// (avant que le tunnel ne se rende) pour ne pas laisser apparaître la page produit du
// tunnel le temps de résoudre la sauvegarde puis de naviguer vers le studio.
const initialSaveId = (() => {
    try {
        return new URLSearchParams(window.location.search).get('save');
    } catch {
        return null;
    }
})();
const isRestoringSave = ref(!!initialSaveId);
const { askEcommerceCustomerId } = usePostMessage();

const isMobile = useMediaQuery(horizontalMediaQueries.mobile);
useEventListener(window, 'message', handleMessage);

// Tient `--mobile-dock-lift` a jour selon le recouvrement reel du bas du
// viewport par l'interface du navigateur (barre d'outils iOS notamment).
useViewportInsets();

onMounted(async () => {
    if (!window.env) {
        console.error('[App] window.env non disponible. Verifiez que env.js est charge.');
    }

    askEcommerceCustomerId();
    await router.isReady();

    // Données variables demandées par l'hôte dans l'URL de l'iframe (`?vdp=1`).
    // Filet par rapport au postMessage `sendEcommerceProduct.vdpEnabled`, qui
    // peut arriver après le premier rendu des outils — même logique que
    // `print_profile_id` pour le verrou de méthode.
    if (route.query.vdp === '1' || route.query.vdp === 'true') {
        productStore.hostVdpEnabled = true;
    }

    // Mockups demandés (ou refusés) par l'hôte dans l'URL de l'iframe
    // (`?mockup=1` / `?mockup=0`). `null` = l'hôte ne s'est pas prononcé : on
    // laisse le réglage du site décider, et un `?mockup=0` explicite doit rester
    // distinct d'une absence de paramètre.
    const mockupFlag = parseMockupQueryFlag(route.query.mockup);
    if (mockupFlag !== null) {
        productStore.hostMockupEnabled = mockupFlag;
    }

    // La liste du FontPicker (catalogue Google Webfonts, ~gros JSON) n'est utile
    // qu'à l'ouverture du sélecteur de police : on ne bloque plus le chargement du
    // design dessus. Le contenu (thème/sauvegarde) résout ses propres polices via
    // ensureFontsLoaded. Chargement en arrière-plan.
    void loadFonts(window.env.VITE_GOOGLE_FONTS_API_KEY).then((fonts) => {
        stageStore.fonts = fonts;
        // Polices IMPORTÉES par l'utilisateur : ré-injectées APRÈS la liste Google
        // (qui vient d'écraser stageStore.fonts) → activées (FontFace) et ajoutées
        // au FontPicker, disponibles dans tous les champs texte.
        void useCustomFonts().loadCustomFonts();
    });

    if (initialSaveId) {
        loadSaveFromId(initialSaveId);
    } else if (isRestoringSave.value) {
        // Sécurité : jamais bloquer sur le loader si l'id a disparu entre-temps.
        isRestoringSave.value = false;
    }

    if (route.query.hiddenCategories) {
        hideCategories(route.query.hiddenCategories as string);
    }
});

function hideCategories(hiddenCategoriesParam: string) {
    const hiddenCategories = hiddenCategoriesParam ? hiddenCategoriesParam.split(',') : [];
    useCategoryStore().hiddenCategories = hiddenCategories;
}

function handleMessage(message: MessageEvent<unknown>) {
    try {
        if (typeof message.data !== 'string') {
            return;
        }

        const payload = message.data.trim();
        // Ignore non-JSON postMessage payloads (e.g. timers/workers from third-party libs like JSZip).
        if (!payload || (payload[0] !== '{' && payload[0] !== '[')) {
            return;
        }

        const data = JSON.parse(payload);
        if (data.eventType === 'sendVariantsData' && data.variantsData) {
            if (stageStore.expectedVariantType === 'product') {
                productStore.updateVariants(data.variantsData);
            } else if (stageStore.expectedVariantType === 'options') {
                siteStore.updateOrderOptionsWithPrices(data.variantsData);
            }
            stageStore.expectedVariantType = null;
        } else if (data.eventType === 'sendVariantsData' && data.error) {
            stageStore.expectedVariantType = null;
            console.error(data.error);
        } else if (data.eventType === 'sendCatalogPrices' && data.prices) {
            // Prix mini des produits catalogue (tunnel e-commerce). Cf. catalogPricing.
            useCatalogPricingStore().applyHostPrices(data.prices);
        } else if (data.eventType === 'sendCustomerId' && data.customerId) {
            stageStore.customerId = data.customerId;
        } else if (data.eventType === 'setInkColors' && Array.isArray(data.colors)) {
            // Couleurs pré-sélectionnées par l'hôte (méthode palette) : l'assistant
            // d'encres les pré-remplit (mappées sur la palette), détection en parallèle.
            productStore.setPresetInkColors(data.colors as string[]);
        } else if (data.eventType === 'mockup') {
            // L'hôte active ou coupe les mockups pour cette session. On accepte le
            // refus explicite (`enabled: false`) : une boutique doit pouvoir les
            // désactiver page par page sans toucher à la configuration du site.
            const enabled = data.enabled ?? data.value;
            if (typeof enabled === 'boolean') {
                productStore.hostMockupEnabled = enabled;
            }
        } else if (data.eventType === 'vdp' && (data.enabled === true || data.value === true)) {
            // Données variables activées par l'hôte → déclenche le guide VDP (modale
            // configurée au BO, affichée une fois par session). Cf. VdpGuideModal.
            stageStore.vdpGuideTrigger += 1;
        } else if (data.eventType === 'sendEcommerceProduct' && data.options && data.variants) {
            // RC3 : ignore une réponse tardive pour un ANCIEN produit (changement rapide
            // de produit) qui écraserait le contexte du produit courant. On compare la
            // base des variantRefs du payload au produit attendu (dernier
            // askEcommerceProduct). Aucune attente (préchargement initial) => accepté.
            const payloadRefs = (data.variants as Array<{ variantRef?: string }>).map((v) => v?.variantRef ?? '');
            if (!productStore.ecommercePayloadMatchesPending(payloadRefs)) {
                console.error('[App] sendEcommerceProduct ignoré : réponse pour un produit périmé', {
                    pending: productStore.pendingEcommerceHandle,
                    payloadFirstRef: payloadRefs[0]
                });
                return;
            }
            const ecommerceData = ref(data);
            const { processEcommerceData } = useProduct();
            processEcommerceData(ecommerceData);
            // Cette redirection ne sert qu'à SORTIR de `/variants/:ref` une fois le
            // produit connu. Dès qu'on est quelque part sous `//products/[productId]/`
            // — page produit OU studio — il n'y a plus rien à faire.
            //
            // L'hôte envoie `sendEcommerceProduct` DEUX fois (préchargement au
            // montage, puis réponse à `askEcommerceProduct`) : avec l'ancien test
            // d'égalité stricte, le second message arrivait après le deep-link
            // `?visual=` qui avait déjà ouvert le studio en parcours simplifié, et
            // ramenait l'utilisateur sur l'étape de choix du tunnel.
            const inProductSection = String(route.name ?? '').startsWith('//products/[productId]/');
            if (inProductSection || !stageStore.configuration.productId) {
                return;
            }
            router.push({
                name: '//products/[productId]/',
                params: { productId: stageStore.configuration.productId },
                query: { visual: route.query.visual, skuTheme: route.query.skuTheme }
            });
        }
    } catch (error) {
        console.warn('[App] Ignored invalid postMessage payload', error);
    }
}

// Point d'entrée unifié de réouverture via ?save={id} : le résolveur backend
// indique s'il s'agit d'un backup (SaveDesign) ou d'une sauvegarde panier (SaveFile).
// On récupère le JSON via l'URL présignée et on aiguille vers le loader adapté.
/** Sauvegarde introuvable/expirée : alerte puis retour au début du parcours. */
async function saveExpiredAndRestart(id: string, reason: string) {
    console.error('[loadSaveFromId] reprise impossible', { id, reason });
    isRestoringSave.value = false; // masque le loader avant la modale
    await confirm({
        title: t('SaveExpiredTitle') || 'Sauvegarde expirée',
        message: t('SaveExpiredMessage') || "Cette sauvegarde a expiré ou n'est plus disponible.",
        confirmMessage: t('Continue') || 'OK'
    });
    // Nettoie le ?save= et repart à l'accueil (début du parcours).
    router.push({ path: '/' });
}

async function loadSaveFromId(id: string) {
    const { data, error } = await useSaves().resolveSave(id);
    if (error.value || !data.value) {
        await saveExpiredAndRestart(id, 'resolve-failed');
        return;
    }
    const { type, fileUrl, productId, content } = data.value as {
        type: string;
        fileUrl: string;
        productId: string | null;
        content?: Record<string, unknown> | null;
    };
    // Contenu inline renvoyé par le backend en priorité (pas de fetch S3 côté
    // navigateur → insensible au CORS, notamment sur les domaines export).
    // Repli : récupération via l'URL présignée si le backend ne l'a pas fourni.
    const fileJson =
        content && typeof content === 'object' ? content : await useSaveDesigns().fetchJsonSaveFromUrl(fileUrl);
    if (!fileJson) {
        await saveExpiredAndRestart(id, 'json-missing');
        return;
    }

    stageStore.configuration.selectedMethod = 'manual';

    // Les polices du design doivent etre pretes avant que le canvas ne le rende :
    // sinon les textes retombent sur la substitution du navigateur, y compris
    // dans le fichier d'impression genere a partir du canvas.
    await ensureFontsLoaded(extractFontFamilies(fileJson));

    if (type === 'order') {
        const orderProductId = productId ?? (fileJson.save as any)?.metadata?.productId ?? null;
        if (!orderProductId) {
            await saveExpiredAndRestart(id, 'order-productId-missing');
            return;
        }
        // Format panier : { save: <objets Fabric>, mediaList, ... }, appliqué via Studio.loadSaveFromJson.
        stageStore.save = {
            json: JSON.stringify(fileJson.save),
            mediaList: Array.isArray(fileJson.mediaList) ? (fileJson.mediaList as string[]) : []
        };
        await router.push({ name: '//products/[productId]/customize', params: { productId: orderProductId } });
    } else {
        // Format backup : { canvasContent: <modèle moteur>, mediaList, ... }, appliqué via Studio.loadDesignFromCanvasContent.
        stageStore.pendingDesign = {
            canvasContent: Array.isArray(fileJson.canvasContent) ? (fileJson.canvasContent as any[]) : [],
            mediaList: Array.isArray(fileJson.mediaList) ? (fileJson.mediaList as string[]) : [],
            // Réécriture backpattern : fond par zone restauré à la reprise (nouveau
            // format) ; les anciennes sauvegardes sont migrées depuis canvasContent
            // au moment de l'application (TresScene). Cf. useBackgroundPattern.
            patternByZone:
                fileJson.patternByZone && typeof fileJson.patternByZone === 'object'
                    ? (fileJson.patternByZone as Record<string, unknown>)
                    : null
        };
        // productId : celui résolu par le backend, sinon celui porté par le JSON.
        const targetProductId =
            productId ?? ((fileJson as any)?.productId || (fileJson as any)?.metadata?.productId) ?? null;
        if (!targetProductId) {
            await saveExpiredAndRestart(id, 'productId-missing');
            return;
        }
        await router.push({ name: '//products/[productId]/customize', params: { productId: targetProductId } });
    }
    // Navigation vers le studio effectuée → le loader plein écran laisse la place à
    // la page produit (qui affiche son propre loader de chargement du produit/design).
    isRestoringSave.value = false;
}

function getConfirmationRoutes(): ConfirmationRoute[] {
    /**
     * Redirection éventuelle (PURE, aucun effet de bord) : un site sans thèmes ni
     * layout n'a rien à faire sur la fiche produit, on l'envoie à ses catégories.
     */
    const leaveCustomizeRedirect = () => {
        if (!siteStore.isFeatureEnabled('Themes') && !defaultSiteConfiguration.isLayoutEnabled) {
            const categoryId = stageStore.configuration.categoryId;
            return { name: '//categories/[categoryId]', params: { categoryId: categoryId ?? '' } };
        }
        return undefined;
    };
    /**
     * ⚠️ Purge du studio — À N'EXÉCUTER QU'APRÈS la navigation (cf. le garde plus bas).
     *
     * `resetChanges()` vide `stageStore.layers`. Or chaque panneau ouvert surveille
     * SON calque et se referme tout seul quand il disparaît, en poussant la route
     * `customize` (cf. customize.[layerId].vue `watch(currentLayer)`). Lancée avant
     * `next()`, cette purge déclenchait donc une navigation CONCURRENTE qui
     * supersédait celle demandée : vue-router annulait « changer de produit » et on
     * restait dans le studio, panneau fermé — le symptôme rapporté, reproductible
     * avec n'importe quel panneau et jamais sans.
     */
    const handleCallbackOnLeaveCustomize = () => {
        stageStore.resetChanges();
    };
    const middlewares: ConfirmationRoute[] = [
        {
            from: '//products/[productId]/customize',
            to: '//products/[productId]/',
            options: {
                title: t('LeaveManager'),
                message: t('LeaveConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            redirect: leaveCustomizeRedirect,
            callback: handleCallbackOnLeaveCustomize
        },
        {
            from: /^\/\/products\/\[[^/]+\]\/(?!customizationType\/?).*/, // Matches any route under //products/[productId]/ except customisationType
            to: '//categories/[categoryId]',
            options: {
                title: t('LeaveManager'),
                message: t('LeaveConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            redirect: leaveCustomizeRedirect,
            callback: handleCallbackOnLeaveCustomize
        },
        {
            from: /^\/\/products\/\[[^/]+\]\/(?!customizationType\/?).*/, // Matches any route under //products/[productId]/ except customisationType
            to: '//',
            // Retour à l'accueil = « Changer de produit » (crumb topbar) → message dédié.
            options: {
                title: t('ChangeProductTitle'),
                message: t('ChangeProductConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            redirect: leaveCustomizeRedirect,
            callback: handleCallbackOnLeaveCustomize
        }
    ];
    const conditionalMiddlewares = isMobile.value
        ? [
              // routes for mobile
              {
                  from: '//products/[productId]/customize',
                  to: '//categories/[categoryId]',
                  options: {
                      title: t('LeaveManager'),
                      message: t('LeaveConfirm'),
                      confirmMessage: t('Confirm'),
                      closeMessage: t('Cancel')
                  },
                  callback: () => {
                      stageStore.resetChanges();
                  }
              }
          ]
        : [
              // routes for desktop
          ];

    return [...middlewares, ...conditionalMiddlewares];
}

router.beforeEach(async (to, from, next) => {
    await initApp();
    const matchedRoute = getConfirmationRoutes().find((route) => {
        let fromMatched = false;
        let toMatched = false;

        if (route.from instanceof RegExp) {
            fromMatched = route.from.test(from.name); // Match dynamic patterns
        } else {
            fromMatched = route.from === from.name;
        }

        if (route.to instanceof RegExp) {
            toMatched = route.to.test(to.name); // Match dynamic patterns
        } else {
            toMatched = route.to === to.name;
        }

        return fromMatched && toMatched;
    });
    if (!matchedRoute) {
        return next();
    }
    if (await confirm(matchedRoute.options)) {
        // La redirection est calculée AVANT (fonction pure) ; le nettoyage, lui, est
        // différé APRÈS la navigation : exécuté ici, il vide les calques, ce qui fait
        // se refermer les panneaux ouverts VIA UNE NAVIGATION concurrente qui annule
        // celle-ci (cf. handleCallbackOnLeaveCustomize).
        const redirect = matchedRoute.redirect?.();
        const cleanup = matchedRoute.callback;
        if (cleanup) {
            const off = router.afterEach(() => {
                off();
                cleanup();
            });
        }
        // `next(fn)` n'a de sens que dans `beforeRouteEnter` : dans un garde GLOBAL,
        // vue-router ignore l'argument fonction — la redirection était donc perdue.
        return next(redirect ?? undefined);
    }
    return next(false);
});
</script>

<style lang="scss">
@import '@/styles/settings.scss';
@import '@/styles/ui-new.scss';
</style>
